import React from "react";
import { Link } from "react-router-dom";
// Same marks SignIn and /connect use, so this empty state can never disagree
// about which agents work.
import { MCP_AGENT_TILES } from "../../../ConnectClaude/connectClients";

// Chrome Web Store listing for the Ultimate Web Scraper extension
const CHROME_WEBSTORE_URL =
  "https://chromewebstore.google.com/detail/ultimate-web-scraper/pdeldjlcnhallaapdggcmhpailpnnkmg";

// ============================================
// CSS Animations (injected into document.head)
// ============================================

const emptyStateStyles = `
@keyframes flowDot {
  0% { left: 0%; opacity: 0; }
  10% { opacity: 1; }
  90% { opacity: 1; }
  100% { left: calc(100% - 6px); opacity: 0; }
}
@keyframes flowDotVertical {
  0% { top: 0%; opacity: 0; }
  10% { opacity: 1; }
  90% { opacity: 1; }
  100% { top: calc(100% - 6px); opacity: 0; }
}
@keyframes cursorMove {
  0%, 100% { transform: translate(16px, -10px); opacity: 0.4; }
  30%, 70% { transform: translate(0, 0); opacity: 1; }
  45%, 55% { transform: translate(1px, 1px); opacity: 1; }
}
@keyframes clickRipple {
  0%, 35% { transform: scale(0); opacity: 0; }
  42% { transform: scale(0.7); opacity: 0.4; }
  55% { transform: scale(1.4); opacity: 0; }
  100% { transform: scale(0); opacity: 0; }
}
@keyframes cloudFloat {
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-5px); }
}
@keyframes pulseRing {
  0% { transform: scale(0.85); opacity: 0.5; }
  100% { transform: scale(1.5); opacity: 0; }
}
@keyframes fadeSlideIn {
  from { opacity: 0; transform: translateY(5px); }
  to { opacity: 1; transform: translateY(0); }
}
`;

if (typeof document !== 'undefined') {
  const styleId = 'empty-state-animations';
  if (!document.getElementById(styleId)) {
    const style = document.createElement('style');
    style.id = styleId;
    style.textContent = emptyStateStyles;
    document.head.appendChild(style);
  }
}

// ============================================
// Step 1: Mini Extension Card
// ============================================

const MiniExtensionCard = () => (
  <div className="relative w-full max-w-[170px] mx-auto">
    <div className="rounded-xl border border-gray-200 dark:border-gray-700/50 bg-white dark:bg-gray-900/70 overflow-hidden shadow-md">
      {/* Browser chrome */}
      <div className="flex items-center gap-1.5 px-2.5 py-1.5 border-b border-gray-100 dark:border-gray-700/40 bg-gray-50 dark:bg-gray-800/60">
        <div className="flex gap-1">
          <div className="w-1.5 h-1.5 rounded-full bg-red-400/60" />
          <div className="w-1.5 h-1.5 rounded-full bg-yellow-400/60" />
          <div className="w-1.5 h-1.5 rounded-full bg-green-400/60" />
        </div>
        <div className="flex-1 ml-1 h-3.5 rounded-md bg-gray-100 dark:bg-gray-700/50 flex items-center px-1.5">
          <span className="text-[5px] text-gray-400 dark:text-gray-500 font-medium tracking-wide">example.com</span>
        </div>
      </div>

      {/* Extension popup content */}
      <div className="p-2.5 space-y-1.5">
        {/* Step row 1 - completed */}
        <div className="flex items-center gap-1.5">
          <div className="w-3.5 h-3.5 rounded-full bg-emerald-100 dark:bg-emerald-500/20 flex items-center justify-center flex-shrink-0">
            <svg className="w-2 h-2 text-emerald-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <div className="flex-1">
            <div className="h-1.5 w-4/5 rounded-full bg-gray-200 dark:bg-gray-700/60" />
          </div>
        </div>
        {/* Step row 2 - completed */}
        <div className="flex items-center gap-1.5">
          <div className="w-3.5 h-3.5 rounded-full bg-emerald-100 dark:bg-emerald-500/20 flex items-center justify-center flex-shrink-0">
            <svg className="w-2 h-2 text-emerald-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <div className="flex-1">
            <div className="h-1.5 w-3/5 rounded-full bg-gray-200 dark:bg-gray-700/60" />
          </div>
        </div>
      </div>

      {/* Action bar with cloud button */}
      <div className="flex items-center gap-1.5 px-2.5 py-2 border-t border-gray-100 dark:border-gray-700/40 bg-gray-50/50 dark:bg-gray-800/30">
        <div className="flex-1 h-5 rounded-md bg-gray-200/70 dark:bg-gray-700/40" />
        {/* Cloud button */}
        <div className="relative">
          <div className="w-5 h-5 rounded-md bg-gradient-to-br from-blue-500 via-cyan-500 to-purple-500 flex items-center justify-center shadow-sm shadow-cyan-500/25">
            <svg className="w-2.5 h-2.5 text-white" fill="currentColor" viewBox="0 0 24 24">
              <path d="M19.35 10.04A7.49 7.49 0 0012 4C9.11 4 6.6 5.64 5.35 8.04A5.994 5.994 0 000 14c0 3.31 2.69 6 6 6h13c2.76 0 5-2.24 5-5 0-2.64-2.05-4.78-4.65-4.96z" />
            </svg>
          </div>
          <div
            className="absolute -inset-1 rounded-lg border border-cyan-400/30"
            style={{ animation: 'clickRipple 3.5s ease-in-out infinite' }}
          />
        </div>
      </div>
    </div>

    {/* Animated cursor */}
    <div
      className="absolute bottom-1.5 right-3 pointer-events-none"
      style={{ animation: 'cursorMove 3.5s ease-in-out infinite' }}
    >
      <svg width="12" height="16" viewBox="0 0 14 18" fill="none">
        <path
          d="M1 1L1 14L4.5 10.5L7.5 17L10 16L7 9L12 8.5L1 1Z"
          className="fill-white dark:fill-gray-200"
          stroke="black"
          strokeWidth="1.2"
          strokeLinejoin="round"
        />
      </svg>
    </div>
  </div>
);

// ============================================
// Step 2: Cloud Processing Card
// ============================================

const CloudProcessingCard = () => (
  <div className="relative w-36 h-36 mx-auto flex items-center justify-center">
    {/* Concentric pulse rings */}
    <div
      className="absolute inset-2 rounded-full border border-cyan-400/20 dark:border-cyan-400/10"
      style={{ animation: 'pulseRing 3s ease-out infinite' }}
    />
    <div
      className="absolute inset-2 rounded-full border border-purple-400/15 dark:border-purple-400/8"
      style={{ animation: 'pulseRing 3s ease-out 1s infinite' }}
    />
    <div
      className="absolute inset-2 rounded-full border border-blue-400/15 dark:border-blue-400/8"
      style={{ animation: 'pulseRing 3s ease-out 2s infinite' }}
    />

    {/* Cloud + Logo with float animation */}
    <div
      className="relative"
      style={{ animation: 'cloudFloat 4s ease-in-out infinite' }}
    >
      <svg className="w-24 h-24" viewBox="-1 0 26 24" fill="none">
        <defs>
          <linearGradient id="emptyCloudGrad" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#3b82f6" />
            <stop offset="50%" stopColor="#06b6d4" />
            <stop offset="100%" stopColor="#a855f7" />
          </linearGradient>
        </defs>
        <path
          d="M19.35 10.04A7.49 7.49 0 0012 4C9.11 4 6.6 5.64 5.35 8.04A5.994 5.994 0 000 14c0 3.31 2.69 6 6 6h13c2.76 0 5-2.24 5-5 0-2.64-2.05-4.78-4.65-4.96z"
          fill="url(#emptyCloudGrad)"
          opacity="0.15"
        />
        <path
          d="M19.35 10.04A7.49 7.49 0 0012 4C9.11 4 6.6 5.64 5.35 8.04A5.994 5.994 0 000 14c0 3.31 2.69 6 6 6h13c2.76 0 5-2.24 5-5 0-2.64-2.05-4.78-4.65-4.96z"
          fill="none"
          stroke="url(#emptyCloudGrad)"
          strokeWidth="0.6"
          opacity="0.7"
        />
      </svg>

      {/* Logo centered on cloud */}
      <img
        src="/logo-256.png"
        alt="Panda Extract"
        className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-10 h-10 drop-shadow-sm"
      />
    </div>
  </div>
);

// ============================================
// Step 3: Dashboard Preview Card
// ============================================

const DashboardPreviewCard = () => (
  <div className="w-full max-w-[185px] mx-auto">
    <div className="rounded-xl border border-gray-200 dark:border-gray-700/50 bg-white dark:bg-gray-900/70 overflow-hidden shadow-md">
      {/* Table header */}
      <div className="flex items-center gap-3 px-2.5 py-1.5 border-b border-gray-100 dark:border-gray-700/30 bg-gray-50/80 dark:bg-gray-800/40">
        <div className="h-1 w-12 rounded-full bg-gray-300 dark:bg-gray-600" />
        <div className="h-1 w-8 rounded-full bg-gray-300 dark:bg-gray-600" />
        <div className="ml-auto h-1 w-6 rounded-full bg-gray-300 dark:bg-gray-600" />
      </div>

      <div className="divide-y divide-gray-50 dark:divide-gray-700/20">
        {/* Completed row */}
        <div
          className="flex items-center gap-2 px-2.5 py-2"
          style={{ animation: 'fadeSlideIn 0.5s ease-out both' }}
        >
          <div className="w-1.5 h-1.5 rounded-full bg-emerald-500 flex-shrink-0" />
          <div className="flex-1 min-w-0">
            <div className="h-1.5 w-3/4 rounded-full bg-gray-200 dark:bg-gray-700/60" />
          </div>
          <span className="text-[7px] font-semibold text-emerald-500 tabular-nums flex-shrink-0">247</span>
        </div>

        {/* Running row */}
        <div
          className="flex items-center gap-2 px-2.5 py-2"
          style={{ animation: 'fadeSlideIn 0.5s ease-out 0.12s both' }}
        >
          <div className="w-1.5 h-1.5 rounded-full bg-amber-500 animate-pulse flex-shrink-0" />
          <div className="flex-1 min-w-0">
            <div className="h-1.5 w-2/3 rounded-full bg-gray-200 dark:bg-gray-700/60" />
          </div>
          <span className="text-[7px] font-semibold text-amber-500 flex-shrink-0">89</span>
        </div>

        {/* Completed row 2 */}
        <div
          className="flex items-center gap-2 px-2.5 py-2"
          style={{ animation: 'fadeSlideIn 0.5s ease-out 0.24s both' }}
        >
          <div className="w-1.5 h-1.5 rounded-full bg-emerald-500 flex-shrink-0" />
          <div className="flex-1 min-w-0">
            <div className="h-1.5 w-1/2 rounded-full bg-gray-200 dark:bg-gray-700/60" />
          </div>
          <span className="text-[7px] font-semibold text-emerald-500 tabular-nums flex-shrink-0">1.2k</span>
        </div>

        {/* Faded placeholder row */}
        <div
          className="flex items-center gap-2 px-2.5 py-2 opacity-30"
          style={{ animation: 'fadeSlideIn 0.5s ease-out 0.36s both' }}
        >
          <div className="w-1.5 h-1.5 rounded-full bg-gray-300 dark:bg-gray-600 flex-shrink-0" />
          <div className="flex-1 min-w-0">
            <div className="h-1.5 w-2/5 rounded-full bg-gray-200 dark:bg-gray-700" />
          </div>
        </div>
      </div>
    </div>
  </div>
);

// ============================================
// Flow Connector (arrow with animated dot)
// ============================================

const FlowConnector = () => (
  <>
    {/* Desktop: horizontal */}
    <div className="hidden lg:flex items-center justify-center w-10 xl:w-14 flex-shrink-0 self-center">
      <div className="relative w-full h-[1.5px]">
        <div className="absolute inset-0 bg-gray-200 dark:bg-gray-700 rounded-full" />
        <div
          className="absolute top-1/2 -translate-y-1/2 w-1.5 h-1.5 rounded-full bg-cyan-400 shadow-sm shadow-cyan-400/50"
          style={{ animation: 'flowDot 2.5s ease-in-out infinite' }}
        />
        <div className="absolute -right-0.5 top-1/2 -translate-y-1/2 w-0 h-0
          border-t-[3px] border-t-transparent
          border-b-[3px] border-b-transparent
          border-l-[5px] border-l-gray-300 dark:border-l-gray-600"
        />
      </div>
    </div>

    {/* Mobile/Tablet: vertical */}
    <div className="lg:hidden flex flex-col items-center py-1">
      <div className="relative h-6 w-[1.5px]">
        <div className="absolute inset-0 bg-gray-200 dark:bg-gray-700 rounded-full" />
        <div
          className="absolute left-1/2 -translate-x-1/2 w-1.5 h-1.5 rounded-full bg-cyan-400 shadow-sm shadow-cyan-400/50"
          style={{ animation: 'flowDotVertical 2.5s ease-in-out infinite' }}
        />
      </div>
      <div className="w-0 h-0
        border-l-[3px] border-l-transparent
        border-r-[3px] border-r-transparent
        border-t-[5px] border-t-gray-300 dark:border-t-gray-600"
      />
    </div>
  </>
);

// ============================================
// Flow Step Card
// ============================================

const stepConfigs = {
  1: {
    gradient: 'from-blue-500 to-blue-600',
    shadow: 'shadow-blue-500/20',
    title: (
      <>
        Use{' '}
        <a
          href={CHROME_WEBSTORE_URL}
          target="_blank"
          rel="noopener noreferrer"
          className="group/link text-blue-500 dark:text-blue-400 hover:text-blue-600 dark:hover:text-blue-300 underline decoration-blue-400/40 hover:decoration-blue-400 underline-offset-2 transition-colors"
        >
          Chrome extension
          <svg
            className="inline-block w-3.5 h-3.5 ml-1 -mt-0.5 opacity-70 group-hover/link:opacity-100 transition-opacity"
            viewBox="0 0 24 24" fill="currentColor" aria-hidden="true"
          >
            <path d="M12 2a10 10 0 0 1 8.66 5H12a5 5 0 0 0-4.9 6.04L3.46 6.77A10 10 0 0 1 12 2zm0 6a4 4 0 1 1 0 8 4 4 0 0 1 0-8zm9.54 1.03A10 10 0 0 1 12 22l4.33-7.5A4.98 4.98 0 0 0 17 12a4.98 4.98 0 0 0-.67-2.5h5.21zM10.46 21.86A10 10 0 0 1 2.7 8.43l4.33 7.5a4.99 4.99 0 0 0 3.43 2.46l0 .01 0 3.46z" />
          </svg>
        </a>
      </>
    ),
    description: 'Set up your extraction in the Chrome extension — select elements, choose pagination',
  },
  2: {
    gradient: 'from-cyan-500 to-cyan-600',
    shadow: 'shadow-cyan-500/20',
    title: 'Runs in the Cloud',
    description: 'Click the cloud button and we handle the rest — 24/7 on our servers',
  },
  3: {
    gradient: 'from-purple-500 to-purple-600',
    shadow: 'shadow-purple-500/20',
    title: 'View Results Here',
    description: 'Your data appears automatically — schedule recurring runs, export anytime',
  },
};

const FlowStep = ({ step, children, featured }) => {
  const config = stepConfigs[step];
  return (
    <div className={`min-w-0 ${featured ? 'flex-1 lg:flex-[1.15]' : 'flex-1'}`}>
      <div className={`
        relative overflow-hidden rounded-2xl h-full
        ${featured ? 'p-5 lg:p-6' : 'p-5 lg:p-6'}
        bg-white/60 dark:bg-gray-900/40
        backdrop-blur-xl
        border
        ${featured
          ? 'border-cyan-200/50 dark:border-cyan-500/15 shadow-lg shadow-cyan-500/8 dark:shadow-cyan-500/5'
          : 'border-gray-200/60 dark:border-gray-700/40 shadow-lg shadow-gray-200/40 dark:shadow-black/20'
        }
        transition-all duration-300
        hover:shadow-xl
      `}>
        {/* Step number badge */}
        <div className={`
          inline-flex items-center justify-center
          w-5 h-5 rounded-full mb-4
          bg-gradient-to-br ${config.gradient}
          text-white text-[10px] font-bold
          shadow-sm ${config.shadow}
        `}>
          {step}
        </div>

        {/* Illustration */}
        <div className="mb-4 flex items-center justify-center min-h-[140px]">
          {children}
        </div>

        {/* Title + Description */}
        <h3 className="text-sm font-bold text-gray-800 dark:text-gray-200 mb-1">{config.title}</h3>
        <p className="text-xs text-gray-500 dark:text-gray-400 leading-relaxed">{config.description}</p>
      </div>
    </div>
  );
};

// ============================================
// Feature Pills
// ============================================

const features = [
  {
    icon: (
      <svg className="w-3.5 h-3.5 text-blue-500 dark:text-blue-400" fill="currentColor" viewBox="0 0 24 24">
        <path d="M19.35 10.04A7.49 7.49 0 0012 4C9.11 4 6.6 5.64 5.35 8.04A5.994 5.994 0 000 14c0 3.31 2.69 6 6 6h13c2.76 0 5-2.24 5-5 0-2.64-2.05-4.78-4.65-4.96z" />
      </svg>
    ),
    title: 'Runs 24/7',
  },
  {
    icon: (
      <svg className="w-3.5 h-3.5 text-cyan-500 dark:text-cyan-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
      </svg>
    ),
    title: 'Schedule runs',
  },
  {
    icon: (
      <svg className="w-3.5 h-3.5 text-purple-500 dark:text-purple-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" />
      </svg>
    ),
    title: 'Faster extraction',
  },
  {
    icon: (
      <svg className="w-3.5 h-3.5 text-violet-500 dark:text-violet-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
      </svg>
    ),
    title: 'Ask your AI',
  },
];

// ============================================
// AI agent invite — alternative onboarding path
// ============================================
// The three steps above are the Chrome-extension flow. Connecting an agent is
// a parallel way in (just ask), not a fourth sequential step — so it lives
// here as its own card, using the same tiles SignIn already shows.

const AgentConnectInvite = ({ showDivider }) => (
  <div className="w-full max-w-xl mt-8">
    {showDivider && (
      <div className="flex items-center gap-3 mb-4" aria-hidden="true">
        <div className="flex-1 h-px bg-gray-200 dark:bg-gray-700/50" />
        <span className="text-[10px] uppercase tracking-[0.2em] font-semibold text-gray-400 dark:text-gray-500">or</span>
        <div className="flex-1 h-px bg-gray-200 dark:bg-gray-700/50" />
      </div>
    )}

    <Link
      to="/dashboard/integrations"
      className="
        group relative flex flex-col sm:flex-row items-center gap-4 p-5
        rounded-2xl
        bg-white/60 dark:bg-gray-900/40 backdrop-blur-xl
        border border-violet-200/50 dark:border-violet-500/15
        shadow-lg shadow-violet-500/5 dark:shadow-black/20
        hover:shadow-xl hover:border-violet-300/70 dark:hover:border-violet-500/30
        transition-all duration-300
      "
    >
      <div className="flex -space-x-1.5 flex-shrink-0" aria-hidden="true">
        {MCP_AGENT_TILES.map(({ name, Mark }) => (
          <div
            key={name}
            title={name}
            className="w-8 h-8 rounded-lg bg-white dark:bg-gray-900 ring-1 ring-black/[0.06] dark:ring-white/10 shadow-sm flex items-center justify-center"
          >
            <Mark className="w-[1.125rem] h-[1.125rem]" />
          </div>
        ))}
      </div>

      <div className="flex-1 min-w-0 text-center sm:text-left">
        <h3 className="text-sm font-bold text-gray-800 dark:text-gray-200">
          Prefer to just ask?
        </h3>
        <p className="text-xs text-gray-500 dark:text-gray-400 leading-relaxed mt-0.5">
          Connect Claude, ChatGPT, Cursor, or any MCP agent and scrape without leaving the chat
        </p>
      </div>

      <span className="inline-flex items-center gap-1.5 text-xs font-semibold text-violet-600 dark:text-violet-400 group-hover:text-violet-700 dark:group-hover:text-violet-300 flex-shrink-0">
        Connect an agent
        <svg className="w-3.5 h-3.5 transition-transform duration-200 group-hover:translate-x-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7l5 5m0 0l-5 5m5-5H6" />
        </svg>
      </span>
    </Link>
  </div>
);

// ============================================
// Filter-Aware Empty State
// ============================================

const STATUS_LABELS = {
  running: 'Running', pending: 'Queued', completed: 'Completed', failed: 'Failed', stopped: 'Stopped',
};
const TYPE_LABELS = {
  extract_list: 'List extraction', extract_page_details: 'Page details',
  extract_pages: 'Page Extractor 2.0',
  extract_emails: 'Email extraction', extract_social_links: 'Social links',
  extract_shopify: 'Shopify extraction',
  extract_woocommerce: 'WooCommerce extraction',
  extract_magento: 'Magento extraction',
  extract_salesforce_commerce: 'Salesforce Commerce extraction',
};

const FilterEmptyState = ({ filters, onClearFilters }) => {
  const parts = [];
  if (filters.status?.length > 0) {
    parts.push(`status: ${filters.status.map(s => STATUS_LABELS[s] || s).join(', ')}`);
  }
  if (filters.taskType) {
    parts.push(`type: ${TYPE_LABELS[filters.taskType] || filters.taskType}`);
  }

  return (
    <div className="flex flex-col items-center justify-center py-20 text-center">
      <div className="w-12 h-12 rounded-2xl bg-gray-100 dark:bg-white/[0.03] border border-gray-200 dark:border-white/[0.06] flex items-center justify-center mb-4">
        <svg className="w-6 h-6 text-gray-400 dark:text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.293A1 1 0 013 6.586V4z" />
        </svg>
      </div>
      <div className="text-gray-700 dark:text-gray-300 text-sm font-semibold mb-1">
        No automations match your filters
      </div>
      {parts.length > 0 && (
        <div className="text-gray-400 dark:text-gray-500 text-xs mb-4 max-w-xs">
          {parts.join('  ·  ')}
        </div>
      )}
      <button
        onClick={onClearFilters}
        className="text-xs font-medium text-cyan-600 dark:text-cyan-400 hover:text-cyan-700 dark:hover:text-cyan-300 transition-colors"
      >
        Clear all filters
      </button>
    </div>
  );
};

const EmptyState = ({ onCreateNew, hasActiveFilters, onClearFilters, filters }) => {
  if (hasActiveFilters) {
    return <FilterEmptyState filters={filters} onClearFilters={onClearFilters} />;
  }

  return (
    <div className="flex flex-col items-center h-full px-4 sm:px-6 lg:px-8 py-8 sm:py-12 max-w-5xl mx-auto">
    {/* Header */}
    <div className="text-center mb-8 lg:mb-10">
      <h2 className="text-2xl sm:text-3xl font-black tracking-tight mb-2">
        <span className="bg-clip-text text-transparent bg-gradient-to-r from-blue-400 via-cyan-400 to-purple-400">
          Run Extractions in the Cloud
        </span>
      </h2>
      <p className="text-gray-500 dark:text-gray-400 text-sm sm:text-base max-w-lg mx-auto">
        Set up in the Chrome extension, run 24/7 on our servers, view results here — or just ask your AI agent
      </p>
    </div>

    {/* Flow Visualization */}
    <div className="w-full mb-8 lg:mb-10">
      <div className="relative">
        {/* Ambient background glows */}
        <div className="absolute -left-16 top-1/2 -translate-y-1/2 w-48 h-48 bg-blue-500/5 dark:bg-blue-500/8 blur-3xl rounded-full pointer-events-none" />
        <div className="absolute left-1/2 -translate-x-1/2 top-1/2 -translate-y-1/2 w-48 h-48 bg-cyan-500/5 dark:bg-cyan-500/6 blur-3xl rounded-full pointer-events-none" />
        <div className="absolute -right-16 top-1/2 -translate-y-1/2 w-48 h-48 bg-purple-500/5 dark:bg-purple-500/8 blur-3xl rounded-full pointer-events-none" />

        {/* Flow steps */}
        <div className="relative flex flex-col lg:flex-row items-center lg:items-stretch gap-3 lg:gap-0">
          <FlowStep step={1}>
            <MiniExtensionCard />
          </FlowStep>

          <FlowConnector />

          <FlowStep step={2} featured>
            <CloudProcessingCard />
          </FlowStep>

          <FlowConnector />

          <FlowStep step={3}>
            <DashboardPreviewCard />
          </FlowStep>
        </div>
      </div>
    </div>

    {/* Feature pills */}
    <div className="flex flex-wrap items-center justify-center gap-2 sm:gap-3 mb-8">
      {features.map((feature) => (
        <div
          key={feature.title}
          className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/60 dark:bg-gray-800/40 border border-gray-200/50 dark:border-gray-700/30 text-xs text-gray-600 dark:text-gray-400"
        >
          {feature.icon}
          <span className="font-medium">{feature.title}</span>
        </div>
      ))}
    </div>

    {/* CTA button */}
    {onCreateNew && (
      <button
        onClick={onCreateNew}
        className="
          inline-flex items-center gap-2.5 px-6 py-3 text-sm font-bold
          bg-gradient-to-r from-blue-500 via-cyan-500 to-purple-500
          hover:from-blue-400 hover:via-cyan-400 hover:to-purple-400
          text-white rounded-xl
          shadow-lg shadow-cyan-500/20
          hover:shadow-xl hover:shadow-cyan-500/30
          transition-all duration-300
          hover:scale-[1.02] active:scale-[0.98]
        "
      >
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M12 4v16m8-8H4" />
        </svg>
        Create Your First Automation
      </button>
    )}

    <AgentConnectInvite showDivider={Boolean(onCreateNew)} />
    </div>
  );
};

export default EmptyState;
